/**
 * Centralized role constants and utilities
 * These match the app_role enum in the database
 */

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  PROGRAM_DIRECTOR: 'program_director',
  MANAGER: 'manager',
  SUPERVISOR: 'supervisor',
  CASE_MANAGER: 'case_manager',
  RESIDENTIAL_AIDE: 'residential_aide',
  STAFF: 'staff',
} as const;

// Type for role values
export type AppRole = typeof ROLES[keyof typeof ROLES];

// Effective role types (simplified 3-role system)
// NOTE: effectiveRole is used for permission grouping only (e.g. "is manager-level or above?").
// Dashboard routing uses the actual userRole so each role gets its dedicated UI.
export type EffectiveRole = 'SUPER_ADMIN' | 'MANAGER' | 'STAFF';

/**
 * Maps database roles to simplified effective roles for UI/UX
 * This does NOT change database structure or RLS policies
 */
export const getEffectiveRole = (role: AppRole | null | undefined): EffectiveRole => {
  if (!role) return 'STAFF';
  
  switch (role) {
    case ROLES.SUPER_ADMIN:
      return 'SUPER_ADMIN';
    // Manager UI: Program Director, Manager, Supervisor, Case Manager
    case ROLES.PROGRAM_DIRECTOR:
    case ROLES.MANAGER:
    case ROLES.SUPERVISOR:
    case ROLES.CASE_MANAGER:
      return 'MANAGER';
    // Staff UI: Residential Aide, Staff
    case ROLES.RESIDENTIAL_AIDE:
    case ROLES.STAFF:
    default:
      return 'STAFF';
  }
};

// Display labels for roles
export const ROLE_LABELS: Record<AppRole, string> = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.PROGRAM_DIRECTOR]: 'Program Director',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.SUPERVISOR]: 'Supervisor',
  [ROLES.CASE_MANAGER]: 'Case Manager',
  [ROLES.RESIDENTIAL_AIDE]: 'Residential Aide',
  [ROLES.STAFF]: 'Staff',
};

// Role hierarchy levels (higher number = more privileges)
export const ROLE_HIERARCHY: Record<AppRole, number> = {
  [ROLES.SUPER_ADMIN]: 7,
  [ROLES.PROGRAM_DIRECTOR]: 6,
  [ROLES.MANAGER]: 5,
  [ROLES.SUPERVISOR]: 4,
  [ROLES.CASE_MANAGER]: 3,
  [ROLES.RESIDENTIAL_AIDE]: 2,
  [ROLES.STAFF]: 1,
};

// Role options for different contexts
export const ROLE_OPTIONS = [
  { value: ROLES.RESIDENTIAL_AIDE, label: ROLE_LABELS[ROLES.RESIDENTIAL_AIDE], adminOnly: false, managerAccess: true },
  { value: ROLES.SUPERVISOR, label: ROLE_LABELS[ROLES.SUPERVISOR], adminOnly: false, managerAccess: true },
  { value: ROLES.STAFF, label: ROLE_LABELS[ROLES.STAFF], adminOnly: false, managerAccess: false },
  { value: ROLES.CASE_MANAGER, label: ROLE_LABELS[ROLES.CASE_MANAGER], adminOnly: false, managerAccess: false },
  { value: ROLES.MANAGER, label: ROLE_LABELS[ROLES.MANAGER], adminOnly: true, managerAccess: false },
  { value: ROLES.PROGRAM_DIRECTOR, label: ROLE_LABELS[ROLES.PROGRAM_DIRECTOR], adminOnly: true, managerAccess: false },
  { value: ROLES.SUPER_ADMIN, label: ROLE_LABELS[ROLES.SUPER_ADMIN], adminOnly: true, managerAccess: false },
];

// Admin-level roles
export const ADMIN_ROLES: AppRole[] = [
  ROLES.SUPER_ADMIN,
  ROLES.PROGRAM_DIRECTOR,
  ROLES.MANAGER,
];

// Helper functions
export const isAdmin = (role: AppRole | null | undefined): boolean => {
  if (!role) return false;
  return ADMIN_ROLES.includes(role as AppRole);
};

export const isSuperAdmin = (role: AppRole | null | undefined): boolean => {
  return role === ROLES.SUPER_ADMIN;
};

export const isProgramDirector = (role: AppRole | null | undefined): boolean => {
  return role === ROLES.PROGRAM_DIRECTOR;
};

export const isManager = (role: AppRole | null | undefined): boolean => {
  return role === ROLES.MANAGER;
};

export const isSupervisor = (role: AppRole | null | undefined): boolean => {
  return role === ROLES.SUPERVISOR;
};

export const canManageRole = (userRole: AppRole | null | undefined, targetRole: AppRole): boolean => {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] > ROLE_HIERARCHY[targetRole];
};

export const getRoleLabel = (role: AppRole | null | undefined): string => {
  if (!role) return 'Unknown';
  return ROLE_LABELS[role] || role;
};

// Helper functions for effective roles
export const isSuperAdminEffective = (role: AppRole | null | undefined): boolean => 
  getEffectiveRole(role) === 'SUPER_ADMIN';

export const isManagerEffective = (role: AppRole | null | undefined): boolean => 
  getEffectiveRole(role) === 'MANAGER';

export const isStaffEffective = (role: AppRole | null | undefined): boolean => 
  getEffectiveRole(role) === 'STAFF';

// Effective role labels for UI display
export const EFFECTIVE_ROLE_LABELS: Record<EffectiveRole, string> = {
  'SUPER_ADMIN': 'Super Admin',
  'MANAGER': 'Manager',
  'STAFF': 'Staff',
};

// Get effective role label for display
export const getEffectiveRoleLabel = (role: AppRole | null | undefined): string => {
  return EFFECTIVE_ROLE_LABELS[getEffectiveRole(role)];
};

export const getRoleBadgeColor = (role: AppRole | null | undefined): string => {
  if (!role) return 'bg-gray-100 text-gray-800 border-gray-200';
  
  switch (role) {
    case ROLES.SUPER_ADMIN:
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case ROLES.PROGRAM_DIRECTOR:
      return 'bg-green-100 text-green-800 border-green-200';
    case ROLES.MANAGER:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case ROLES.SUPERVISOR:
      return 'bg-red-100 text-red-800 border-red-200';
    case ROLES.CASE_MANAGER:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case ROLES.RESIDENTIAL_AIDE:
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case ROLES.STAFF:
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};
